import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import { useTranslation } from "@coworker/locales";
import { useMemo, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router";
import Linkbar from "../../../shared/components/Linkbar/Linkbar";
import {
  FixaOption,
  FixaSelect,
} from "../../../shared/wrappers/FixaSelect/FixaSelect";
import { FixaText } from "../../../shared/wrappers/FixaText/FixaText";
import { NotFound } from "../../../task-management/components/NotFound/NotFound";
import {
  MediaAboutFields,
  type MediaAboutFieldValues,
} from "../../components/MediaAboutFields/MediaAboutFields";
import { MediaDetailCarousel } from "../../components/MediaDetailCarousel/MediaDetailCarousel";
import type { MediaCardProps } from "../../components/MediaCard/MediaCard";
import { MediaPublicationInfo } from "../../components/MediaPublicationInfo/MediaPublicationInfo";
import { ProductCard } from "../../components/ProductCard/ProductCard";
import { TagList } from "../../components/TagList/TagList";
import styles from "../media-detail-page.module.css";
import { MEDIA_TYPE_OPTIONS, mediaCards, reasonForChange } from "../mock-media";
import type { LaunchMedia } from "../../types/launch";
import type { ExtractedMedia } from "../../services/uploadPresentation";
import { useItemsInfoBatch } from "../../../shared/hooks/item/useItemsInfo";
import { useMedia } from "../../hooks/useMedia";
import { useMediaArticles } from "../../hooks/useMediaArticles";
import { useMediaImages } from "../../hooks/useMediaImages";
import {
  mapArticlesToProducts,
  mapAttributesToAboutFieldValues,
} from "../../utils/mediaDetailMappers";
import { normalizeMediaType } from "../../utils/mediaType";

const mapLaunchMediaToCard = (media: LaunchMedia): MediaCardProps => ({
  id: media.id,
  imageUrl: media.preview_images?.[0]?.url ?? "",
  imageAlt: media.name,
  badgeLabel: "",
  title: media.name,
  fiscalYear: media.fiscal_year ?? "",
  mediaType: normalizeMediaType(media.media_type),
  location: media.location ?? "",
  status: media.status ?? "Unpublished",
  publishedDate: media.published_date ?? "",
});

// Mirrors the "Life at home" activity taxonomy used across the Media Management POC.
const LIFE_AT_HOME_ACTIVITIES = [
  "Socialise",
  "Cook",
  "Wash & Clean",
  "Eat",
  "Hobby",
  "Store & Organise",
  "Play, Create & Learn",
  "Work",
  "Come & Go",
  "Sleep",
  "Personal Care",
  "Stay Healthy",
];

export const MediaDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const extractedMedia = (
    location.state as { extractedMedia?: ExtractedMedia } | null
  )?.extractedMedia;

  const { data: fetchedMedia, isLoading: isLoadingMedia } = useMedia(id);
  const media = useMemo(
    () =>
      fetchedMedia
        ? mapLaunchMediaToCard(fetchedMedia)
        : // TODO: Remove mock fallback once all media are served by the API.
          mediaCards.find((card) => card.id === id),
    [fetchedMedia, id]
  );
  const extractedMediaCard: MediaCardProps | undefined = extractedMedia
    ? {
        id: id ?? `extracted-${extractedMedia.source.slide_number}`,
        imageUrl: "",
        imageAlt: "",
        badgeLabel: `Slide ${extractedMedia.source.slide_number}`,
        title: extractedMedia.name,
        fiscalYear: "",
        mediaType: normalizeMediaType(extractedMedia.media_type),
        location: "",
        status: "Unpublished" as const,
        publishedDate: "",
      }
    : undefined;
  const displayedMedia = extractedMediaCard ?? media;

  const [mediaTypeDraftsByMediaId, setMediaTypeDraftsByMediaId] = useState<
    Record<string, string>
  >({});
  const { data: mediaArticles } = useMediaArticles(id);
  const mediaAttributes = fetchedMedia?.attributes;
  const { data: mediaImages, isLoading: isLoadingMediaImages } =
    useMediaImages(id);
  const articleItemNos = useMemo(
    () => (mediaArticles ?? []).map((article) => article.product_article_id),
    [mediaArticles]
  );
  const { data: articleItems } = useItemsInfoBatch(articleItemNos);
  const products = useMemo(
    () =>
      id && mediaArticles
        ? mapArticlesToProducts(mediaArticles, articleItems ?? [], id)
        : [],
    [id, mediaArticles, articleItems]
  );
  const mediaType = displayedMedia
    ? (mediaTypeDraftsByMediaId[displayedMedia.id] ?? displayedMedia.mediaType)
    : "";

  const onMediaTypeChange = (nextMediaType: string) => {
    if (!displayedMedia) return;

    setMediaTypeDraftsByMediaId((currentDrafts) => ({
      ...currentDrafts,
      [displayedMedia.id]: nextMediaType,
    }));
  };

  const mainMessage =
    mediaAttributes?.find(({ attribute }) => attribute === "main_message")
      ?.value ?? "-";

  const mediaAboutInitialValues = useMemo(
    () => ({
      // Only received attributes are set; missing fields use MediaAboutFields fallbacks.
      ...(displayedMedia?.location
        ? { location: displayedMedia.location }
        : {}),
      ...mapAttributesToAboutFieldValues(mediaAttributes),
    }),
    [displayedMedia, mediaAttributes]
  );
  const [mediaAboutDraftsByMediaId, setMediaAboutDraftsByMediaId] = useState<
    Record<string, MediaAboutFieldValues>
  >({});

  const mediaAboutValues = useMemo(
    () => ({
      ...mediaAboutInitialValues,
      ...(displayedMedia
        ? mediaAboutDraftsByMediaId[displayedMedia.id]
        : undefined),
    }),
    [displayedMedia, mediaAboutDraftsByMediaId, mediaAboutInitialValues]
  );

  const onMediaAboutChange = (nextValues: MediaAboutFieldValues) => {
    if (!displayedMedia) return;

    setMediaAboutDraftsByMediaId((currentDrafts) => ({
      ...currentDrafts,
      [displayedMedia.id]: nextValues,
    }));
  };

  const [activityTags, setActivityTags] = useState(LIFE_AT_HOME_ACTIVITIES);
  const [selectedActivityTags, setSelectedActivityTags] = useState<string[]>(
    []
  );
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [newTagValue, setNewTagValue] = useState("");

  const toggleActivityTag = (tag: string) => {
    setSelectedActivityTags((current) =>
      current.includes(tag)
        ? current.filter((selected) => selected !== tag)
        : [...current, tag]
    );
  };

  const handleAddTag = () => {
    const trimmedTag = newTagValue.trim();

    if (trimmedTag && !activityTags.includes(trimmedTag)) {
      setActivityTags((current) => [...current, trimmedTag]);
      setSelectedActivityTags((current) => [...current, trimmedTag]);
    }

    cancelAddTag();
  };

  const cancelAddTag = () => {
    setNewTagValue("");
    setIsAddingTag(false);
  };

  const linkbarItems = useMemo(
    () => [{ label: t("capitalCaseLaunchMediaString"), to: "/launch-media" }],
    [t]
  );

  const enableLaunchMedia = useFlag(FLAGS.ENABLE_LAUNCH_MEDIA);
  if (!enableLaunchMedia) {
    return <Navigate to="/home" replace />;
  }

  if (!displayedMedia) {
    if (isLoadingMedia) {
      return <main className={styles["page"]} aria-busy="true" />;
    }

    return (
      <main className={styles["page"]}>
        <NotFound
          title={t("mediaNotFoundString")}
          message={t("noResultsString")}
        />
      </main>
    );
  }

  const images = mediaImages?.map(({ url }) => url) ?? [];

  return (
    <main className={styles["page"]}>
      <Linkbar
        items={linkbarItems}
        currentLabel={displayedMedia.title}
        currentAsH1
      />

      <section className={styles["pageContent"]}>
        {!isLoadingMediaImages && (
          <MediaDetailCarousel
            images={images}
            title={displayedMedia.title}
            imageAlt={displayedMedia.imageAlt}
          />
        )}

        <FixaText tagName="h2" className={styles["mediaTitle"]}>
          {t("workBriefString")}
        </FixaText>

        <div className={styles["field"]}>
          <FixaInputField
            id={"name"}
            type={"text"}
            value={displayedMedia.title}
            readOnly
            label={t("nameString")}
          />
        </div>

        <div className={styles["field"]}>
          <FixaSelect
            id="mediaTypeSelect"
            label={t("selectMediaTypeString")}
            value={mediaType}
            onChange={(e) => onMediaTypeChange(e.target.value)}
          >
            {MEDIA_TYPE_OPTIONS.map((option) => (
              <FixaOption key={option} value={option} name={option} />
            ))}
          </FixaSelect>
        </div>

        <div className={styles["field"]}>
          <FixaText className={styles["title"]}>
            {t("reasonForChangeString")}
          </FixaText>

          <FixaTextArea
            id={"reasonForChange"}
            value={reasonForChange}
            readOnly
            label={t("whyMediaNeedsToBeChangedString")}
          />
        </div>

        <div className={styles["field"]}>
          <FixaText className={styles["title"]}>
            {t("mainMessageMainActivityString")}
          </FixaText>

          <FixaTextArea
            className={styles["mainMessageMainActivityComponent"]}
            id={"mainMessageMainActivity"}
            value={mainMessage}
            readOnly
            label={t("specificNeedToBeSolvedString")}
          />
        </div>

        <MediaAboutFields
          mediaType={mediaType}
          location={displayedMedia.location}
          values={mediaAboutValues}
          onChange={onMediaAboutChange}
        />

        <section className={styles["field"]}>
          <FixaText className={styles["title"]}>
            {t("lifeHomeActivityString")}
          </FixaText>

          <TagList
            tags={activityTags}
            selectedTags={selectedActivityTags}
            onToggleTag={toggleActivityTag}
            isAddingTag={isAddingTag}
            newTagValue={newTagValue}
            onNewTagValueChange={setNewTagValue}
            onStartAddTag={() => setIsAddingTag(true)}
            onConfirmAddTag={handleAddTag}
            onCancelAddTag={cancelAddTag}
          />
        </section>

        <section className={styles["field"]}>
          <FixaText tagName="h2" className={styles["title"]}>
            {t("product2String")}
          </FixaText>

          <div className={styles["productsGrid"]}>
            {products.map(({ item, article }) => (
              <ProductCard key={article.id} item={item} article={article} />
            ))}
          </div>
        </section>

        <MediaPublicationInfo
          published={displayedMedia.status === "Published"}
          publishedDate={displayedMedia.publishedDate}
          onPublishToggle={() => {}}
        />
      </section>
    </main>
  );
};
